import React, { useEffect, useContext, useState } from 'react';

import {
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';

import * as yup from 'yup';

import {
  useHistory,
  useParams,
  withRouter,
} from 'react-router-dom';

import { Field, Formik } from 'formik';

import PropTypes from 'prop-types';

import BasicButton from '../../buttons/basicButton';
import BreadCrumb from '../../fields/breadCrumb';
import CustomSnackbar from '../../feedback/customSnackbar';
import Date from '../../fields/variables/date';
import Footer from '../../fields/footer';
import Header from '../../fields/header';
import Integer from '../../fields/variables/integer';
import MenuDrawer from '../../fields/menuDrawer';
import Multiple from '../../fields/variables/multiple';
import Number from '../../fields/variables/number';
import Single from '../../fields/variables/single';
import SpinnerLoading from '../../feedback/spinnerLoading';
import String from '../../fields/variables/string';
import TranslationWarning from '../../modals/translationWarning';

import { getInclusionCriterias, getPatientInfo, getProgramInfo, getUserSchedule, newResultSet } from '../../../utils/comms';

import { buildResultSet, createYupSchema, evaluateComplexRule, evaluateSimpleRule, computeOperation } from '../../../utils/helpers';

import translations from '../../../languages/i18n';
import { addProgramTranslations }  from '../../../languages/i18n';

import { sectionTitle } from '../../../styles/formElementsStyles';

import generalStyles from '../../../styles/generalStyles';

// NOTA: Per poder emprar el react-router-dom i enllaçar pàgines s'ha de convertir el component en una funció i exportar-lo embolicat per withRouter
// En cas de que el component sigui un fill directe de Route no fa falta fer-ho
// Explicat al punt 2.1. Using withRouter de https://dev.to/projectescape/programmatic-navigation-in-react-3p1l

import { SessionContext } from '../../../utils/context';

function FormInfo({ lang, handleChangeLang }) {
  const sessionContext = useContext(SessionContext);

  const [emptyResultSet, setEmptyResultSet] = useState(false);
  const [enrolmentId, setEnrolmentId] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [formName, setFormName] = useState('');
  const [formSent, setFormSent] = useState(false);
  const [inclusionCriteria, setInclusionCriteria] = useState([]);
  const [infoReady, setInfoReady] = useState(false);
  const [patientInfo, setPatientInfo] = useState('');
  const [schedulingPatternId, setSchedulingPatternId] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showSendButton, setShowSendButton] = useState(false);
  const [showTranslationWarning, setShowTranslationWarning] = useState(false);
  const [userForm, setUserForm] = useState([]);

  const history = useHistory();
  const { programId, userScheduleId } = useParams();

  const classes = generalStyles();
  const sectionTitleStyle = sectionTitle();

  const handleCloseDialog = () => {
    setShowTranslationWarning(false);
  };

  const manageFeedback = (fbType, fbMessage) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
  };

  useEffect(() => {
    const getInfoFromServer = async () => {
      setShowLoading(true);
      const responseFromServer = await Promise.all([
        getUserSchedule(userScheduleId, sessionContext.userToken),
        getInclusionCriterias(sessionContext.userToken),
        getProgramInfo(programId, sessionContext.userToken)
      ]);
      // Comprovam si dins les respostes n'hi ha alguna de timeOut o erronia
      const allOk = responseFromServer.map((answer) => {
        if (answer.status && answer.status === 200) return 'ok';
        if (answer === 'timeOut') return 'timeOut';
        return 'error';
      });
      const idTO = allOk.indexOf('timeOut');
      const idE = allOk.indexOf('error');

      let translationsError = false;
      if (idTO > -1 || idE > -1) { // Al manco hi ha 1 timeOut o 1 error
        setShowLoading(false);
        // Miram si algun dels errors rebuts és un timeOut
        if (idTO > -1) { // Hi ha hagut un timeOut
          manageFeedback('error', 'TimeOut');
        } else { // Hi ha hagut un error
          manageFeedback('error', 'Generic');
        }
      } else { // No hi ha cap error

        try {
          await addProgramTranslations(programId, sessionContext.userToken);
        } catch (error) {
          translationsError = true;
          setShowTranslationWarning(true);
        }

        /* +++++++++++ Obtenim el nom del pacient per mostrar per pantalla +++++++++++ */
        const responsePatientInfo = await getPatientInfo(responseFromServer[0].data.userSchedule.enrolment.patient, sessionContext.userToken);
        if (responsePatientInfo.status === 200) setPatientInfo(responsePatientInfo.data.patient);
        // No tractam el cas de que s'hagi produït un error demanant la informació del pacient ja que si hem arribat fins aquí tenim tota la informació necessària
        // per mostrar el formulari i simplement no mostrarem el nom del pacient (ni s'aplicaran els criteris d'inclusió relatius al sexe i edat com si aquesta inforamció no s'hagués introduit al servidor)

        /* +++++++++++ Obtenim les variables que s'han de mostrar +++++++++++ */
        // ATENCIÓ: abaix, per saber si userSchedule conté la propietat resultsDate en lloc de fer response[0].data.userSchedule.hasOwnProperty('resultsDate')
        // emplei la manera recomanada a la regla https://eslint.org/docs/rules/no-prototype-builtins de l'eslint.
        setEmptyResultSet(!Object.prototype.hasOwnProperty.call(responseFromServer[0].data.userSchedule, 'resultsDate'));
        setEnrolmentId(responseFromServer[0].data.userSchedule.enrolment);
        setFormName(responseFromServer[0].data.userSchedule.name);
        setSchedulingPatternId(responseFromServer[0].data.userSchedule.schedulingPattern);
        setUserForm(responseFromServer[0].data.userSchedule.variables);

        /* +++++++++++ Obtenim els criteris d'inclusió del programa +++++++++++ */
        // OPCIÓ 1
        // ATENCIÓ: Modificar la funció showField() en concordància amb l'opció finalment triada
        // Agafam els criteris del programa amb el que estam treballant i que són de tipus client
        // const programClientInclusionCriteria = response[1].data.inclusionCriteria.filter((iCriteria) => (iCriteria.program === programId && iCriteria.type === 'client'));
        // setInclusionCriteria(programClientInclusionCriteria);
        // console.log('programClientInclusionCriteria val:');
        // console.log(programClientInclusionCriteria);

        // OPCIÓ 2
        // ATENCIÓ: Modificar la funció showField() en concordància amb l'opció finalment triada
        // Agafam els criteris del programa amb el que estam treballant
        const programInclusionCriteria = responseFromServer[1].data.inclusionCriteria.filter((iCriteria) => iCriteria.program === programId);

        // TODO: quedar-nos només amb els inclusionCriteria que estiguin dins l'array de variables (alerta que dins cada variable hi pot haver més d'una variable, mira com he emprat flatMap a listClinicians)

        setInclusionCriteria(programInclusionCriteria);
        // console.log('programInclusionCriteria val:');
        // console.log(programInclusionCriteria);

        /* +++++++++++ Obtenim el tipus d'usuari +++++++++++ */
        let typeOfUser = '';
        if (sessionContext.role === 'Admin') {
          typeOfUser = 'AppAdmin';
        } else if (sessionContext.role !== 'Patient') {
          // Agafam els ids dels administradors del programa
          const progAdministratorIds = responseFromServer[2].data.program.accessibleBy.clinicians.filter((clinician) => clinician.admin === true);
          // Extreim el clínic de l'equip clínic
          const memberObj = responseFromServer[0].data.userSchedule.enrolment.supervisedBy.find((member) => member.clinician === sessionContext.userId);
          // Si memberObj és undefined vol dir que l'usuari clínic no està dins l'equip de l'enrolment, per tant només pot haver arribat aquí essent administrador del programa
          if (!memberObj) {
            typeOfUser = 'Administrator';
          } else if (memberObj.admin) { // Miram si és l'usuari clinic supervisor de l'equip. Feim aquesta comprovació primer per donar prioritat al fet que sigui supervisor sobre que sigui administrador del programa
            typeOfUser = 'Supervisor';
          } else { // Miram si l'usuari està dins el llistat d'aministradors del programa
            typeOfUser = progAdministratorIds.find((administratorId) => administratorId.clinician._id === sessionContext.userId) ? 'Administrator' : 'Basic';
          }
        }
        /* +++++++++++ Decidim si el formulari es podrà contestar +++++++++++ */
        // Si el formulari és del propi usuari el podrá contestar
        if (responseFromServer[0].data.userSchedule.user === sessionContext.userId) {
          setShowSendButton(true);
        // Si el formulari NO és del propi usuari, només es podrà contestar si l'usuari és un clínic supervisor i el formulari NO és d'un pacient
        } else if (typeOfUser === 'Supervisor') {
          // Comprovam si l'usuari al que està assignat el formulari és un pacient
          const response = await getPatientInfo(responseFromServer[0].data.userSchedule.user, sessionContext.userToken);
          // Si els servidor ens torna un 404 vol dir que no ha trobat cap pacient amb aquest identificador,
          // per tant, el formulari no és d'un pacient i el supervisor el pot contestar
          if (response === 404) setShowSendButton(true);
        }
        setShowLoading(false);      
      }
      if (!translationsError) setInfoReady(true);      
    };
    getInfoFromServer();
  }, []);

  const goBack = () => {
    history.goBack();
  };

  const gotoScreen = (screen) => {
    history.push(`/${sessionContext.role.toLowerCase()}/${screen}`);
  };

  const loadFormikInitialValues = (variables) => {
    const formikInitialValues = {};
    if (emptyResultSet) {
      variables.map((variable) => formikInitialValues[variable.sharedId] = '');
    } else {
      variables.map((variable) => {
        // ATENCIÓ: abaix, per saber si variable conté la propietat value en lloc de fer variable.prototype.hasOwnProperty('value') emplei la manera recomanada
        // a la regla https://eslint.org/docs/rules/no-prototype-builtins de l'eslint.
        formikInitialValues[variable.sharedId] = Object.prototype.hasOwnProperty.call(variable, 'value') ? variable.value : '';
      });
    }
    return formikInitialValues;
  };

  const loadValidationSchema = (variables) => {
    const yupSchema = variables.reduce(createYupSchema, {});
    const validateSchema = yup.object().shape(yupSchema);
    return validateSchema;
  };

  const sendForm = async (values) => {
    setShowLoading(true);
    const resultSet = buildResultSet(values, userForm, userScheduleId);
    const response = await newResultSet({ resultSet }, sessionContext.userToken);
    setShowLoading(false);
    if (response.status === 200) { // ResultSet enviat
      // TO DO: decidir què fer si s'han enviat bé els resultats
      // De moment, si el formulari s'ha enviat NO mostrarm el botó d'enviar i canviam el text del botó de cancel
      setFormSent(true);
      manageFeedback('success', 'SentForm');
    } else if (response === 400) { // Error amb les dades
      manageFeedback('error', 'SentForm');
    } else { // Login KO. Altres errors
      console.log(response);
    }
  };

  const showField = (sharedId, values) => {
    // Obtenim la variable associada al sharedId
    const variable = userForm.find((v) => v.sharedId === sharedId);
    // Si la variable no té cap criteri d'inclusió la mostram (retornam true)
    if (variable.inclusionCriteria.length === 0) return true;
    // Obtenim les regles dels criteris de la variable
    const varCriteriasRule = variable.inclusionCriteria.map((vic) => inclusionCriteria.find((ic) => ic._id === vic).rule);
    // Avaluam les regles de la variable
    const fulfillsRules = varCriteriasRule.map((vcr) => {
      if (vcr === '????') return true; // Fins haver decidit què fer, si el criteri es de tipus server (rule: "????") mostram la variable

      let fulfillsThisRule = false;
      // Si dins la regla del criteri hi ha la propietat rules, vol dir que és una regla complexa
      if ('rules' in vcr) {
        fulfillsThisRule = evaluateComplexRule(values, vcr, patientInfo);
      } else {        
        let value = null;
        if (vcr.variable.operation) //el valor a comparar és un valor compost (suma de variables...)        
          value = computeOperation(vcr.variable, values);                                    
        else
          value = values[vcr.variable];
        fulfillsThisRule = evaluateSimpleRule(value, vcr.value, vcr.variable, vcr.type, patientInfo);                       
      }
      return fulfillsThisRule;
    });
    // Dins fulfillsRules tenim un array de true/false segons el resultat d'avaluar cada regla
    // si hi ha alguna regla que no es compleix (hi ha algun false a l'array), no mostrarem la variable
    const fulfillsAllRules = !fulfillsRules.some((rule) => rule === false);
    // Si no hem de mostrar la variable li assignam (al values del Formik) el seu valor per defecte, per si un cas hi havia algun valor anterior en cas de que abans s'hagi mostrat la variable
    if (!fulfillsAllRules) values[sharedId] = '';
    return fulfillsAllRules;
  };

  const replaceFieldValues = (variableSharedId, values, value) => {
    values[variableSharedId] = value; // És el values del formik que li pass des de la cridada a la funció del component. Necessari per a que el component Multiple passi l'array de values al formik
  };

  const showMenuDrawer = (show) => {
    setShowDrawer(show);
  };

  const typeOfComponent = (type) => {
    let component = '';
    switch (type) {
      case 'Date':
        component = Date;
        break;
      case 'Integer':
        component = Integer;
        break;
      case 'Multiple':
        component = Multiple;
        break;
      case 'Number':
        component = Number;
        break;
      case 'Single':
        component = Single;
        break;
      case 'String':
        component = String;
        break;
      // TODO: llevar en haver comprovat si empram String o Free Text, només deixar una opcio
      case 'Free text':
        component = String;
        break;
    }
    return component;
  };

  return (
    (infoReady && (
      <Formik
        initialValues={loadFormikInitialValues(userForm)}
        validationSchema={loadValidationSchema(userForm)}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values) => { sendForm(values); }}
      >
        {({ values, touched, errors, handleChange, submitForm }) => (
          <div className={classes.content}>
            <SpinnerLoading show={showLoading} />
            <TranslationWarning handleClose={handleCloseDialog} message={translations['_formsTranslationsNotAvailable']} open={showTranslationWarning} title={translations['_alert']} />
            <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && feedbackMessage) || ''} />
            <Header type={sessionContext.role.toLowerCase()} lang={lang} handleChangeLang={handleChangeLang} handleShowDrawer={(show) => showMenuDrawer(show)} />
            <MenuDrawer type={sessionContext.role.toLowerCase()} showDrawer={showDrawer} handleShowDrawer={(show) => showMenuDrawer(show)} handleGotoScreen={(screen) => gotoScreen(screen)} />
            <div className={classes.body}>
              <Grid container spacing={2} justify='space-around'>
                {/* ----------------------- Fila 0 ----------------------- */}
                <Grid item xs={12}>
                  <BreadCrumb role={sessionContext.role.toLowerCase()} home={sessionContext.role === 'Patient' ? 'listPatientForms' : 'listPatients'} lang={lang} />
                </Grid>
                {/* ----------------------- Fila 1 ----------------------- */}
                <Grid item xs={11} className={sectionTitleStyle.root}>
                  <Grid container justify='flex-start'>
                    <Grid item>
                      <Typography variant='h4' component='div' className={sectionTitleStyle.title}>{translations['_form']}: {translations[formName]}</Typography>
                      <Typography variant='h5' component='div' className={sectionTitleStyle.title}>{translations['_patient']}: {patientInfo.username}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {/* ----------------------- Fila 2 ----------------------- */}
                <Grid item xs={11}>
                  <Grid container component={Paper} justify='flex-start' alignItems='flex-end' spacing={4}>
                    {(infoReady && userForm.length !== 0 && userForm.map((variable) => (
                      showField(variable.sharedId, values) && (
                        <Grid item xs={12} key={variable._id}>
                          <Field
                            key={`var_${variable._id}`}
                            name={variable.sharedId}
                            sharedId={variable.sharedId}
                            // Cap d'aquestes maneres m'ha anat bé:
                            // component={Date}
                            // component={variable.dataType}
                            // component={React.createElement(variable.dataType, null)}
                            // Ho he hagut de fer així:
                            // (dins variable.dataType hi pot haver un objecte o un string)
                            component={variable.dataType.type ? typeOfComponent(variable.dataType.type) : typeOfComponent(variable.dataType)}
                            // (si es un tipo "complex" de dades dins values hi ha les opcions)
                            options={variable.dataType.values || null}
                            handleResult={(event) => handleChange(event)}
                            handleResultMultiple={(result) => replaceFieldValues(variable.sharedId, values, result)} // Pel cas del component Multiple
                          />
                        </Grid>
                      )
                    ))) || (infoReady && userForm.length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant='body1' component='div'>{translations['_noVarsForYou']}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                {/* ----------------------- Fila 3 ----------------------- */}
                <Grid item xs={11} className={sectionTitleStyle.root}>
                  <Grid container spacing={2} direction='row' justify='center'>
                    <Grid item>
                      {!formSent && showSendButton && userForm.length !== 0 && <BasicButton handleClick={submitForm} type='accept' />}
                    </Grid>
                    <Grid item>
                      <BasicButton handleClick={goBack} type='cancel' text={!formSent ? translations['_cancel'] : translations['_continue']} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <Footer />
          </div>
        )}
      </Formik>
    )) || (
      <div className={classes.content}>
        <SpinnerLoading show={showLoading} />
        <TranslationWarning handleClose={handleCloseDialog} message={translations['_formsTranslationsNotAvailable']} open={showTranslationWarning} title={translations['_alert']} />
        <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && feedbackMessage) || ''} />
        <Header type={sessionContext.role.toLowerCase()} lang={lang} handleChangeLang={handleChangeLang} handleShowDrawer={(show) => showMenuDrawer(show)} />
        <MenuDrawer type={sessionContext.role.toLowerCase()} showDrawer={showDrawer} handleShowDrawer={(show) => showMenuDrawer(show)} handleGotoScreen={(screen) => gotoScreen(screen)} />
        <div className={classes.body}>
          <Grid container spacing={2} justify='space-around'>
            {/* ----------------------- Fila 0 ----------------------- */}
            <Grid item xs={12}>
              <BreadCrumb role={sessionContext.role.toLowerCase()} home={sessionContext.role === 'Patient' ? 'listPatientForms' : 'listPatients'} lang={lang} />
            </Grid>
            {/* ----------------------- Fila 1 ----------------------- */}
            <Grid item xs={11} className={sectionTitleStyle.root}>
              <Grid container justify='flex-start'>
                <Grid item>
                  <Typography variant='h4' component='div' className={sectionTitleStyle.title}>{translations['_form']} {translations[formName]} {translations['_fromPatient']} {patientInfo.username}</Typography>
                </Grid>
              </Grid>
            </Grid>
            {/* ----------------------- Fila 2 ----------------------- */}
            <Grid item xs={11}>
              <Grid container component={Paper} justify='flex-start' alignItems='flex-end' spacing={4}>
                <Grid item xs={12}>
                  <Typography variant='body1' component='div'>{translations['_noVarsForYou']}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <Footer />
      </div>
    )
  );
}

export default withRouter(FormInfo);

// Indica i controla els props que se han de passar al component
FormInfo.propTypes = {
  handleChangeLang: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
};
